import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import './index.css';
import IntegrationToggle from './IntegrationToggle';
import ConnectionStatus from './ConnectionStatus';
import { deleteIntegration, getIntegrations, testIntegrationConnection, setPrimaryIntegration } from '../../store/Integration';

/**
 * IntegrationBox - Renders a single integration box dynamically (supports multiple accounts)
 */
const IntegrationBox = ({ tool, integrations = [], canManageIntegrations }) => {

    const dispatch = useDispatch();
    // Get test status from integrationTest state (keyed by integrationId)
    const integrationTest = useSelector(state => state['Integration']?.['integrationTest']) || {};

    const [errorMessage, setErrorMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [disconnectingIntegration, setDisconnectingIntegration] = useState(null);

    const hasConnections = integrations.length > 0;

    // Handle disconnection
    const handleDisconnect = async () => {
        if (!disconnectingIntegration || !disconnectingIntegration['_id']) return;

        try {
            await dispatch(deleteIntegration(disconnectingIntegration['_id']));
            dispatch(getIntegrations());
        } catch (error) {
            console.error(`Failed to disconnect ${tool['name']}:`, error);
            setErrorMessage(`Failed to disconnect ${tool['name']}. Please try again.`);
            setShowErrorModal(true);
        }
        setShowDisconnectModal(false);
        setDisconnectingIntegration(null);
    };

    // Open disconnect confirmation modal
    const openDisconnectModal = (integration) => {
        setShowDisconnectModal(true);
        setDisconnectingIntegration(integration);
    };

    // Test connection for a specific integration
    const handleTestConnection = (integrationId) => {
        if (!integrationId || integrationTest[integrationId]?.['loading']) return;
        dispatch(testIntegrationConnection(integrationId));
    };

    // Handle set primary
    const handleSetPrimary = (integrationId) => dispatch(setPrimaryIntegration(integrationId));

    // Standard integration rendering
    return (
        <div className='col-lg-6 col-md-6 col-sm-12'>
            <div className='integrations-box'>
                <div className='icon'>
                    {tool['logo'] 
                        ? <img src={tool['logo']} alt={tool['name']} className="tool-logo" />
                        : <span>{tool['name']}</span>
                    }
                </div>
                <div className='text-box'> <p>{tool['description']}</p> </div>
                <div className='bottom-btns-area bottom-btns-area-column'>
                    {hasConnections ? (
                        // Connected state - show all accounts
                        <div className="accounts-container">
                            {/* Connected Accounts List */}
                            {integrations.map((integration, index) => {
                                
                                const accountId = integration['accountIdentifier'];
                                const isLastItem = index === integrations.length - 1;
                                return (
                                    <div key={integration['_id']} className="account-row" style={isLastItem ? { borderBottom: 'none' } : undefined}>
                                        {/* Account Info */}
                                        <div className="account-info">
                                            <i className='fa fa-check-circle account-checkmark'></i>
                                            <span className="account-identifier"> {accountId || 'Connected'} </span>
                                            {integration['isPrimary'] && <span className="primary-badge"> (Default) </span>}
                                        </div>

                                        {/* Actions */}
                                        <div className="account-actions">
                                            {/* Test button - shown for tools that support testing (like Twilio) */}
                                            {tool['authMethod'] === 'apiKey' && (
                                                <button
                                                    className="action-btn"
                                                    title="Test Connection"
                                                    disabled={integrationTest[integration['_id']]?.['loading']}
                                                    onClick={() => handleTestConnection(integration['_id'])}
                                                >
                                                    {integrationTest[integration['_id']]?.['loading'] 
                                                    ? <i className='fa fa-spinner fa-spin'></i>
                                                    : <i className='fa fa-plug'></i> }
                                                </button>
                                            )}
                                            {/* Default selection - radio button style */}
                                            {integrations.length > 1 && canManageIntegrations && (
                                                <button
                                                    title={integration['isPrimary'] ? "Default Account" : "Set as Default"}
                                                    onClick={() => !integration['isPrimary'] && handleSetPrimary(integration['_id'])}
                                                    className={`primary-select-btn ${integration['isPrimary'] ? 'is-primary' : ''}`}
                                                >
                                                    <span className={`primary-indicator ${integration['isPrimary'] ? 'active' : ''}`}>
                                                        {integration['isPrimary'] && <i className='fa fa-check'></i> }
                                                    </span>
                                                </button>
                                            )}
                                            {/* Visibility Toggle */}
                                            {integration['isOwned'] && canManageIntegrations &&
                                                <IntegrationToggle
                                                    disabled={false}
                                                    integrationName={tool['name']}
                                                    integrationId={integration['_id']}
                                                    onUpdate={() => dispatch(getIntegrations())}
                                                    visibilityType={integration['visibilityType'] || 'private'}
                                                />
                                            }
                                            {/* Delete button - after visibility toggle */}
                                            {canManageIntegrations &&
                                                <button
                                                    title="Remove Account"
                                                    className="action-btn"
                                                    onClick={() => openDisconnectModal(integration)}
                                                >
                                                    <i className='fa fa-trash'></i>
                                                </button>
                                            }
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Add Another Account - using ConnectionStatus */}
                            {canManageIntegrations &&
                                <ConnectionStatus
                                    toolName={tool['name']}
                                    onConnect={() => dispatch(getIntegrations())}
                                    showAddOnly={true}
                                />
                            }
                        </div>
                    ) : (
                        // Disconnected state - show connect button using ConnectionStatus
                        canManageIntegrations ? (
                            <ConnectionStatus
                                toolName={tool['name']}
                                onConnect={() => dispatch(getIntegrations())}
                                showConnectOnly={true}
                            />
                        ) : <span className="contact-admin-text"> Contact an admin to connect integrations </span>
                    )}
                </div>
            </div>

            {/* Disconnect Confirmation Modal */}
            <Modal
                isOpen={showDisconnectModal}
                toggle={() => {
                    setShowDisconnectModal(false);
                    setDisconnectingIntegration(null);
                }}
                className="main-modal new-user-modal modal-dialog-centered"
            >
                <ModalHeader toggle={() => {
                    setShowDisconnectModal(false);
                    setDisconnectingIntegration(null);
                }}>
                    <h3>Disconnect {tool['name']}?</h3>
                </ModalHeader>
                <ModalBody>
                    <div className="modal-area mt-4">
                        <h5 className="mb-0">Are you sure you want to disconnect this {tool['name']} account?</h5>
                        {disconnectingIntegration && (
                            <p className="modal-account-info">
                                <i className='fa fa-info-circle'></i>
                                Account: {disconnectingIntegration?.['accountIdentifier'] || 'N/A'}
                            </p>
                        )}
                        <p className="modal-warning-text">
                            This will remove all {tool['name']} functionality from your workflows and automation.
                        </p>
                    </div>
                    <div className="group-btns mt-4 d-flex justify-content-end ">
                        <button
                            className="btn-style-border mr-2"
                            onClick={() => {
                                setShowDisconnectModal(false);
                                setDisconnectingIntegration(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-style-new"
                            onClick={handleDisconnect}
                        >
                            <i className='icon fa fa-trash'></i> Disconnect
                        </button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Error Modal */}
            <Modal
                isOpen={showErrorModal}
                toggle={() => setShowErrorModal(false)}
                className="main-modal new-user-modal modal-dialog-centered"
            >
                <ModalHeader toggle={() => setShowErrorModal(false)}>
                    <h3>Error</h3>
                </ModalHeader>
                <ModalBody>
                    <div className="modal-area">
                        <p>{errorMessage}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        className="btn-style-new"
                        onClick={() => setShowErrorModal(false)}
                    >
                        OK
                    </button>
                </ModalFooter>
            </Modal>
        </div>
    )
}

export default IntegrationBox;
